import { useTranslation } from "react-i18next";

/**
 * Renders any of the three legal docs (Terms, Privacy, Cancellation) by
 * reading section copy from the `legal` i18n namespace. The `prefix` ("t"
 * | "p" | "c") + `countKey` decides which keys to walk.
 */
type Props = {
  /** i18n key for the page heading, e.g. "legal.termsTitle". */
  titleKey: string;
  /** i18n key for the intro paragraph. Optional. */
  introKey?: string;
  /** Section-key prefix: "t", "p" or "c". */
  prefix: "t" | "p" | "c";
  /** i18n key for the section count, e.g. "legal.termsSectionsCount". */
  countKey: string;
  /** ISO date string of last legal review. */
  lastUpdated: string;
};

export default function LegalDocLayout({ titleKey, introKey, prefix, countKey, lastUpdated }: Props) {
  const { t } = useTranslation("legal");
  const total = parseInt(t(countKey), 10) || 0;

  const sections = Array.from({ length: total }, (_, i) => i + 1);

  return (
    <div className="min-h-screen bg-offwhite pt-14 md:pt-20">
      <div className="w-full px-4 md:px-8 lg:px-12 py-10 md:py-16">
        <div className="max-w-3xl mx-auto">
          <h1 className="font-display text-2xl md:text-3xl font-bold text-charcoal mb-2">
            {t(titleKey)}
          </h1>
          <p className="text-sm text-gray-400 mb-8">{t("legal.lastUpdated")} {lastUpdated}</p>

          {/* Legal-entity header card — required disclosure for both documents */}
          <div className="bg-white rounded-2xl border border-gray-100 p-5 md:p-6 mb-6">
            <h2 className="text-[11px] uppercase tracking-wider text-gray-400 mb-2">
              {t("legal.entityHeading")}
            </h2>
            <p className="text-sm font-semibold text-charcoal">{t("legal.entityName")}</p>
            <p className="text-xs text-gray-600 mt-1">{t("legal.entityRfc")}</p>
            <p className="text-xs text-gray-600">{t("legal.entityRegime")}</p>
            <p className="text-xs text-gray-600 mt-1">{t("legal.entityAddress")}</p>
            <p className="text-xs text-gray-600 mt-1">{t("legal.entityContact")}</p>
            <p className="text-xs text-gray-500 italic mt-2">{t("legal.entityBrand")}</p>
          </div>

          {introKey && (
            <p className="text-sm text-gray-600 leading-relaxed mb-6 px-1">
              {t(introKey)}
            </p>
          )}

          <div className="bg-white rounded-2xl border border-gray-100 p-6 md:p-8 space-y-8">
            {sections.map((n) => (
              <section key={n}>
                <h2 className="text-base font-bold text-charcoal mb-3">
                  {t(`legal.${prefix}${n}`)}
                </h2>
                <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">
                  {t(`legal.${prefix}${n}Text`)}
                </p>
              </section>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
